import { useCallback } from 'react'

import { analyticsService, datadogService, legacyAnalyticsService } from 'lib/insights'

/**
 * Custom hook for comprehensive telemetry tracking
 * Provides easy-to-use methods for analytics and monitoring
 *
 * @example
 * ```tsx
 * const { track, identify, captureException } = useTelemetry()
 *
 * // Track user actions
 * track('button_clicked', { button_name: 'submit' })
 *
 * // Identify users
 * identify('user123', { plan: 'premium' })
 *
 * // Capture errors
 * try {
 *   await riskyOperation()
 * } catch (error) {
 *   captureException(error, { component: 'UserProfile' })
 * }
 * ```
 */
export const useTelemetry = () => {
  /**
   * Track custom events for analytics
   * Use this for user interactions, feature usage, and business metrics
   *
   * @param eventName - Name of the event (use snake_case)
   * @param properties - Additional event data
   *
   * @example
   * ```tsx
   * // Track button clicks
   * track('button_clicked', { button_name: 'submit', page: 'login' })
   *
   * // Track feature usage
   * track('feature_used', { feature_name: 'export', format: 'pdf' })
   *
   * // Track user actions
   * track('search_performed', { query_length: 25, filters_applied: 3 })
   * ```
   */
  const track = useCallback(
    (eventName: string, properties?: Record<string, any>) => {
      legacyAnalyticsService.track({ name: eventName, properties: properties || {} })
    },
    []
  )

  /**
   * Track events with the new Mixpanel v2 analytics service.
   * Returns false when the service is not ready yet so callers can retry if needed.
   */
  const mixpanelTrack = useCallback(
    (eventName: string, properties?: Record<string, any>) => {
      if (!analyticsService.isReady()) {
        return false
      }

      analyticsService.track({ name: eventName, properties: properties || {} })
      return true
    },
    []
  )

  /**
   * Identify a user for analytics and monitoring
   * Call this when user logs in or when you have user information
   *
   * @param userId - Unique user identifier
   * @param properties - User attributes (plan, role, etc.)
   *
   * @example
   * ```tsx
   * // Basic user identification
   * identify('user123')
   *
   * // With user properties
   * identify('user123', {
   *   plan: 'premium',
   *   role: 'admin',
   *   signup_date: '2024-01-15'
   * })
   * ```
   */
  const identify = useCallback(
    (userId: string, properties?: Record<string, any>) => {
      const userData = { id: userId, properties: properties || {} }
      legacyAnalyticsService.identify(userData)
      datadogService.identify(userData)
    },
    []
  )

  /**
   * Link anonymous device ID to authenticated user ID
   * IMPORTANT: Call this ONLY on first-time login/signup, BEFORE calling identify()
   * Use in conjunction with checkIfFirstLogin() utility to prevent duplicate calls
   *
   * @param userId - Unique user identifier to link to current anonymous session
   *
   * @example
   * ```tsx
   * // On first login
   * import { checkIfFirstLogin, markUserAsIdentified } from '@utils/mixpanel-identity'
   *
   * if (checkIfFirstLogin(userId)) {
   *   alias(userId) // Link anonymous events to this user
   *   markUserAsIdentified(userId) // Prevent future alias calls
   * }
   * identify(userId, userProperties) // Always call after alias
   * ```
   */
  const alias = useCallback((userId: string) => {
    legacyAnalyticsService.alias(userId)
  }, [])

  /**
   * Get the current Mixpanel distinct ID (device ID or user ID)
   * Useful for debugging identity linking
   *
   * @returns The current distinct ID, or null if Mixpanel is not initialized
   *
   * @example
   * ```tsx
   * // Check current identity
   * const currentId = getDistinctId()
   * console.log('Current Mixpanel ID:', currentId)
   * ```
   */
  const getDistinctId = useCallback(() => {
    return legacyAnalyticsService.getDistinctId()
  }, [])

  /**
   * Update user properties for analytics and monitoring
   * Use this to update user attributes after initial identification
   *
   * @param properties - User attributes to update
   *
   * @example
   * ```tsx
   * // Update user plan
   * setUserProperties({ plan: 'enterprise', upgrade_date: '2024-01-20' })
   *
   * // Update user preferences
   * setUserProperties({
   *   theme: 'dark',
   *   language: 'en',
   *   notifications_enabled: true
   * })
   * ```
   */
  const setUserProperties = useCallback((properties: Record<string, any>) => {
    legacyAnalyticsService.setUserProperties(properties)
    // Datadog user properties are updated via identify() call
  }, [])

  /**
   * Track page views for analytics
   * Use this for navigation tracking and page performance analysis
   *
   * @param path - Page path or URL
   * @param properties - Additional page data
   *
   * @example
   * ```tsx
   * // Basic page view
   * trackPageView('/dashboard')
   *
   * // With page metadata
   * trackPageView('/products', {
   *   category: 'electronics',
   *   page_title: 'Product Catalog',
   *   user_segment: 'premium'
   * })
   * ```
   */
  const trackPageView = useCallback(
    (path: string, properties?: Record<string, any>) => {
      legacyAnalyticsService.trackPageView(path, properties)
    },
    []
  )

  /**
   * Capture and track errors for debugging and monitoring
   * Use this in try-catch blocks and error boundaries
   *
   * @param error - The error object to capture
   * @param context - Additional context about where the error occurred
   *
   * @example
   * ```tsx
   * // Basic error capture
   * try {
   *   await riskyOperation()
   * } catch (error) {
   *   captureException(error)
   * }
   *
   * // With context
   * try {
   *   await saveUserData(userData)
   * } catch (error) {
   *   captureException(error, {
   *     component: 'UserProfile',
   *     action: 'save',
   *     user_id: userData.id
   *   })
   * }
   * ```
   */
  const captureException = useCallback(
    (error: Error, context?: Record<string, any>) => {
      // Track in both analytics and monitoring for comprehensive error tracking
      legacyAnalyticsService.captureException(error, context)
      datadogService.trackError(error, context)
    },
    []
  )

  /**
   * Reset user identification and clear user data
   * Use this when user logs out or when switching users
   *
   * @example
   * ```tsx
   * // On logout
   * const handleLogout = () => {
   *   reset()
   *   // ... other logout logic
   * }
   * ```
   */
  const reset = useCallback(() => {
    legacyAnalyticsService.reset()
    datadogService.clearUser()
  }, [])

  /**
   * Start timing an event for performance measurement
   * Use this to measure how long operations take
   *
   * @param eventName - Name of the event to time
   *
   * @example
   * ```tsx
   * // Start timing a search operation
   * startTiming('search_duration')
   * await performSearch()
   * endTiming('search_duration', { query_length: query.length })
   * ```
   */
  const startTiming = useCallback((eventName: string) => {
    legacyAnalyticsService.startTiming(eventName)
  }, [])

  /**
   * End timing an event and track the duration
   * Use this to complete performance measurements
   *
   * @param eventName - Name of the event to end timing
   * @param properties - Additional properties to include with the timing data
   *
   * @example
   * ```tsx
   * // Complete timing measurement
   * endTiming('search_duration', {
   *   results_count: results.length,
   *   search_type: 'advanced'
   * })
   * ```
   */
  const endTiming = useCallback(
    (eventName: string, properties?: Record<string, any>) => {
      legacyAnalyticsService.endTiming(eventName, properties)
    },
    []
  )

  // Advanced Mixpanel features
  const mixpanel = {
    setSuperProperties: useCallback((properties: Record<string, any>) => {
      legacyAnalyticsService.setSuperProperties(properties)
    }, []),

    setSuperPropertiesOnce: useCallback((properties: Record<string, any>) => {
      legacyAnalyticsService.setSuperPropertiesOnce(properties)
    }, []),

    startTiming: useCallback((eventName: string) => {
      legacyAnalyticsService.startTiming(eventName)
    }, []),

    endTiming: useCallback(
      (eventName: string, properties?: Record<string, any>) => {
        return legacyAnalyticsService.endTiming(eventName, properties)
      },
      []
    ),

    trackFunnelStep: useCallback(
      (
        stepName: string,
        funnelName: string,
        properties?: Record<string, any>
      ) => {
        legacyAnalyticsService.trackFunnelStep(stepName, funnelName, properties)
      },
      []
    ),

    increment: useCallback((property: string, value: number = 1) => {
      legacyAnalyticsService.increment(property, value)
    }, []),

    alias: useCallback((distinctId: string) => {
      legacyAnalyticsService.alias(distinctId)
    }, []),

    getDistinctId: useCallback(() => {
      return legacyAnalyticsService.getDistinctId()
    }, []),
  }

  // Citation engagement tracking helpers
  const citationTracking = {
    startCitationFunnel: useCallback(
      (queryId: string, query: string) => {
        const funnelName = 'citation_engagement'

        // Set session context
        mixpanel.setSuperProperties({
          current_query_id: queryId,
          funnel_session: funnelName,
        })

        // Start timing
        mixpanel.startTiming('citation_engagement_duration')

        // Track query send
        track('query_send', {
          query,
          query_length: query.length,
          query_id: queryId,
          timestamp: new Date().toISOString(),
        })

        // Start funnel
        mixpanel.trackFunnelStep('query_send', funnelName, {
          query_id: queryId,
          query_length: query.length,
        })
      },
      [track, mixpanel]
    ),

    trackCitedSentenceClick: useCallback(
      (
        queryId: string,
        citationId: string,
        sentenceText: string,
        citationCount: number
      ) => {
        track('cited_sentence_click', {
          query_id: queryId,
          citation_id: citationId,
          sentence_length: sentenceText.length,
          citation_count: citationCount,
          sentence_preview: sentenceText.substring(0, 100),
          timestamp: new Date().toISOString(),
        })

        mixpanel.trackFunnelStep(
          'cited_sentence_click',
          'citation_engagement',
          {
            citation_id: citationId,
            citation_count: citationCount,
          }
        )

        mixpanel.trackFunnelStep('sentence_click', 'sentence_to_card_funnel', {
          query_id: queryId,
          citation_id: citationId,
        })
      },
      [track, mixpanel]
    ),

    trackCitationBubbleClick: useCallback(
      (
        queryId: string,
        citationId: string,
        bubbleNumber: number,
        citationsInSentence: number
      ) => {
        track('citation_bubble_click', {
          query_id: queryId,
          citation_id: citationId,
          bubble_number: bubbleNumber,
          citations_in_sentence: citationsInSentence,
          timestamp: new Date().toISOString(),
        })

        mixpanel.trackFunnelStep(
          'citation_bubble_click',
          'citation_engagement',
          {
            citation_id: citationId,
            bubble_number: bubbleNumber,
          }
        )

        mixpanel.trackFunnelStep('bubble_click', 'bubble_to_card_funnel', {
          query_id: queryId,
          citation_id: citationId,
        })
      },
      [track, mixpanel]
    ),

    trackSourceCardClick: useCallback(
      (
        queryId: string,
        sourceId: string,
        clickSource: 'direct' | 'cited_sentence' | 'citation_bubble',
        sourceMetadata?: Record<string, any>
      ) => {
        track('source_card_click', {
          query_id: queryId,
          source_id: sourceId,
          click_source: clickSource,
          source_title: sourceMetadata?.title,
          source_type: sourceMetadata?.type,
          source_year: sourceMetadata?.year,
          timestamp: new Date().toISOString(),
        })

        mixpanel.trackFunnelStep('source_card_click', 'citation_engagement', {
          source_id: sourceId,
          click_source: clickSource,
        })

        // Complete path-specific funnels
        if (clickSource === 'cited_sentence') {
          mixpanel.trackFunnelStep('card_opened', 'sentence_to_card_funnel', {
            source_id: sourceId,
          })
        } else if (clickSource === 'citation_bubble') {
          mixpanel.trackFunnelStep('card_opened', 'bubble_to_card_funnel', {
            source_id: sourceId,
          })
        }

        // End timing
        mixpanel.endTiming('citation_engagement_duration', {
          final_action: 'source_card_click',
          click_source: clickSource,
        })
      },
      [track, mixpanel]
    ),

    trackGenerationComplete: useCallback(
      (
        queryId: string,
        metadata: {
          sourceCount: number
          citationCount: number
          generationLength: number
          processingTime: number
        }
      ) => {
        track('generation_complete', {
          query_id: queryId,
          source_count: metadata.sourceCount,
          citation_count: metadata.citationCount,
          generation_length: metadata.generationLength,
          processing_time_ms: metadata.processingTime,
          timestamp: new Date().toISOString(),
        })

        mixpanel.setSuperProperties({
          last_generation_sources: metadata.sourceCount,
          last_generation_citations: metadata.citationCount,
        })
      },
      [track, mixpanel]
    ),
  }

  return {
    // Core telemetry methods
    track,
    mixpanelTrack,
    identify,
    alias,
    getDistinctId,
    setUserProperties,
    trackPageView,
    captureException,
    reset,
    isInitialized: legacyAnalyticsService.isReady(),

    // Advanced Mixpanel features
    mixpanel,

    // Domain-specific tracking helpers
    citationTracking,
    startTiming,
    endTiming,
  }
}
